
import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import DemoPageHeader from "@/components/DemoPageHeader";
import OrdersTable from "@/components/OrdersTable";
import OrdersFilters from "@/components/OrdersFilters";
import { DEMO_ORDERS } from "@/utils/demoData";
import { EtsyOrder } from "@/hooks/useEtsyOrders";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const DemoOrders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [demoOrders, setDemoOrders] = useState<EtsyOrder[]>(DEMO_ORDERS);
  const [filteredOrders, setFilteredOrders] = useState<EtsyOrder[]>(DEMO_ORDERS);

  // Update filtered orders when orders change
  useEffect(() => {
    setFilteredOrders(demoOrders);
  }, [demoOrders]);

  // Apply search filter to the already filtered orders
  const searchFilteredOrders = filteredOrders.filter((order: EtsyOrder) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      order.receiptId.toLowerCase().includes(searchLower) ||
      order.customerName.toLowerCase().includes(searchLower) ||
      order.productName.toLowerCase().includes(searchLower) ||
      order.city.toLowerCase().includes(searchLower) ||
      order.status.toLowerCase().includes(searchLower) ||
      (order.customerEmail && order.customerEmail.toLowerCase().includes(searchLower))
    );
  });

  const handleOrderUpdate = (updatedOrder: EtsyOrder) => {
    const updatedOrders = demoOrders.map(order => 
      order.receiptId === updatedOrder.receiptId ? updatedOrder : order
    );
    setDemoOrders(updatedOrders);
  };

  const handleOrderAdd = (newOrder: EtsyOrder) => {
    const updatedOrders = [newOrder, ...demoOrders];
    setDemoOrders(updatedOrders);
  };

  return (
    <DashboardLayout>
      <DemoPageHeader
        title="Demo Orders"
        description="Experience how you would manage all your Etsy orders in one place."
      >
        {/* Demo Banner */}
        <Card className="border-blue-200 bg-blue-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Demo Order Management</h3>
                <p className="text-blue-800 text-sm mb-3">
                  You're viewing {demoOrders.length} sample orders. Try filtering, searching, editing orders, and adding new orders 
                  to see how you would manage your actual Etsy orders.
                </p>
                <Link to="/signup">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                    Connect Your Real Store
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        <OrdersFilters 
          searchTerm={searchTerm} 
          onSearchChange={setSearchTerm}
          orders={demoOrders}
          onFiltersChange={setFilteredOrders}
          onOrderAdd={handleOrderAdd}
        />

        <OrdersTable 
          orders={searchFilteredOrders}
          totalOrders={demoOrders.length}
          isConnected={true}
          isFetchingOrders={false}
          onOrderUpdate={handleOrderUpdate}
        />
      </DemoPageHeader>
    </DashboardLayout>
  );
};

export default DemoOrders;
